import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';

import { queryClient } from '../components/queryClient';
import { useFleet, useTimezone } from '../hooks';
import { Device, EventsFilter } from '../interfaces';
import { EventsService } from '../services';
import { DeviceStatusService } from '../services/device-status.service';

export const useDeviceStatus = () => {
  const fleet = useFleet();
  const queryKey = ['deviceStatus', fleet?.id];

  const queryFn = () => DeviceStatusService.getDeviceStatuses();

  return useQuery({
    queryKey,
    queryFn,
    staleTime: 1000 * 30, // 30 seconds
    enabled: !!fleet?.id,
  });
};

export const useLastObservation = (deviceId: string) => {
  const fleet = useFleet();
  const timezone = useTimezone();
  const queryKey = ['lastObservation', deviceId];

  const queryFn = async () => {
    const filter: EventsFilter = {
      dateFrom: moment.tz(0, moment.tz.guess()).toISOString(),
      dateTo: moment.tz(new Date(), moment.tz.guess()).tz(timezone, true).toISOString(),
      limit: 1,
      device_ids: [deviceId],
    };
    const lastEvent = await EventsService.getEvents(filter, fleet);

    if (lastEvent?.events?.length > 0) {
      return lastEvent.events[0].captured_at;
    }
    return null;
  }

  return useQuery({
    queryKey,
    queryFn,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!fleet?.id && !!deviceId,
  });
}

export const useHighlightedDeviceId = () => {
  return useQuery({
    queryKey: ['highlightedDeviceId'],
    queryFn: () => Promise.resolve<Device['id'] | null>(null)
  });
};

export const useSelectedDeviceId = () => {
  return useQuery({
    queryKey: ['selectedDeviceId'],
    queryFn: () => Promise.resolve<Device['id'] | null>(null)
  });
};


export const setHighlightedDeviceId = (id: Device['id'] | null) => {
  queryClient.setQueryData(['highlightedDeviceId'], id);
};

export const setSelectedDeviceId = (id: Device['id'] | null) => {
  queryClient.setQueryData(['selectedDeviceId'], id);
};
